import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { UseQueryParameters } from '~/types/ApiRequestTypes'

import {
  ApiError,
  PaginatedApiResult,
  User,
  UsersQueryParams,
} from '@mangovoice/types'

interface UseGetPbxUsersParams extends UseQueryParameters<
  PaginatedApiResult<User>
> {
  pbxId: number
  _queryParams: UsersQueryParams
}

export const useGetPbxUsers = ({
  pbxId,
  onQueryStarted,
  onQuerySuccess,
  onQueryCancelled,
  onQueryError,
  options,
  _queryParams,
}: UseGetPbxUsersParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  const [queryParams, setQueryParams] = useState<UsersQueryParams>(_queryParams)

  const getQueryKey = () => {
    const key = ['users', pbxId, { ...queryParams }]
    return key
  }

  const query = useQuery<PaginatedApiResult<User>, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (pbxId === 0) {
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params = {
        pbx_id: pbxId,
        limit: queryParams.limit,
        offset: queryParams.offset,
        search: queryParams.search,
        ordering: queryParams.ordering?.join(','),
      }

      const { ok, data, status } = await api.users.listSelect(params)

      if (!ok || !data) {
        const error = {
          ...(data as object),
          status,
        }
        if (!signal?.aborted) {
          onQueryError?.(error)
        } else {
          onQueryCancelled?.()
        }
        return Promise.reject(error)
      }

      onQuerySuccess?.(data)
      return data
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? false,
  })

  const tryCancelQuery = () => {
    const queryKey = getQueryKey()
    queryClient.cancelQueries({
      queryKey,
    })
  }

  const refetch = useCallback(
    (newParams?: UsersQueryParams) => {
      tryCancelQuery()
      if (newParams) {
        setQueryParams(newParams)
      }
      query.refetch()
    },
    [query],
  )

  const results = useMemo(() => {
    return query.data?.results
  }, [query.data])

  const totalCount = useMemo(() => {
    return query.data?.count
  }, [query.data])

  useEffect(() => {
    query.refetch()
  }, [queryParams, pbxId])

  return {
    ...query,
    results,
    totalCount,
    refetch,
    tryCancelQuery,
  }
}
